import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../api/client';
import { authHeaders, devGetMe, ServiceInfo } from '../api/dev';
import Spinner from '../components/Spinner';
import WebhookConfigSection from '../components/WebhookConfigSection';
import AccessPolicyCard from '../components/AccessPolicyCard';
import GeoMapModal from '../components/GeoMapModal';
import OnboardingModal from '../components/OnboardingModal';

type BBoxUI = { minLat: string; minLon: string; maxLat: string; maxLon: string; name?: string };
type CircleUI = { lat: string; lon: string; radiusKm: string; name?: string };
type PolygonUI = { coords: string; name?: string }; // WKT POLYGON string

type Config = {
  webhookUrl: string;
  enabled?: boolean;
  apiKeyId?: string;
  regions?: {
    bboxes?: Array<{ minLat: number; minLon: number; maxLat: number; maxLon: number; name?: string }>;
    circles?: Array<{ lat: number; lon: number; radiusKm: number; name?: string }>;
    polygons?: Array<{ points: Array<{ lat: number; lon: number }>; name?: string }>;
  };
};

export default function DevLightningPush() {
  // Me/meta
  const [services, setServices] = useState<string[]>([]);
  const [serviceInfo, setServiceInfo] = useState<ServiceInfo[]>([]);
  const [policy, setPolicy] = useState<any>(null);
  const allowed = useMemo(() => new Set(services), [services]);
  const svcId = 'lightning-push';
  const meta = useMemo(() => serviceInfo.find(s => s.id === svcId), [serviceInfo]);
  const title = meta?.name || 'Lightning Push Notifications';
  const description = meta?.description || 'Webhook alerts for lightning events.';

  // Config basics
  const [config, setConfig] = useState<Config>({ enabled: true, webhookUrl: '' });
  const [hasSecret, setHasSecret] = useState<boolean>(false);
  const [apiKeyId, setApiKeyId] = useState<string>('');
  const [hasApiKey, setHasApiKey] = useState<boolean>(false);

  // Regions UI state
  const [bboxes, setBboxes] = useState<BBoxUI[]>([]);
  const [circles, setCircles] = useState<CircleUI[]>([]);
  const [polygons, setPolygons] = useState<PolygonUI[]>([]);
  const [okRegions, setOkRegions] = useState('');
  const [errRegions, setErrRegions] = useState('');
  const [showRegionsMap, setShowRegionsMap] = useState(false);
  const [showAccessGuide, setShowAccessGuide] = useState(false);
  const [showWebhookGuide, setShowWebhookGuide] = useState(false);
  const [showRegionsGuide, setShowRegionsGuide] = useState(false);
  const loc = useLocation();
  const tab = useMemo<'about' | 'access' | 'webhook' | 'regions'>(() => {
    const p = String(loc?.pathname || '');
    if (p.includes('/lightning-push/access')) return 'access';
    if (p.includes('/lightning-push/webhook')) return 'webhook';
    if (p.includes('/lightning-push/regions')) return 'regions';
    return 'about';
  }, [loc?.pathname]);

  // Page state
  const [loadingPage, setLoadingPage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function parseNum(s: string): number { return parseFloat(String(s).trim()); }

  useEffect(() => {
    (async () => {
      setLoadingPage(true);
      try {
        // Load meta
        const me = await devGetMe();
        setServices(me.services || []);
        setServiceInfo(me.serviceInfo || []);
        setPolicy(me.policy || null);

        // Load config
        const resp = await apiFetch('/dev/lightning-push/config', { headers: { ...authHeaders() } });
        const data = await resp.json().catch(() => ({}));
        const cfg = (data.config || {}) as Config;
        setConfig({ enabled: !!cfg.enabled, webhookUrl: cfg.webhookUrl || '', apiKeyId: cfg.apiKeyId });
        setHasSecret(!!data.hasSecret);
        const loadedApiKeyId = String(data.apiKeyId || cfg.apiKeyId || '');
        setApiKeyId(loadedApiKeyId);
        setHasApiKey(Boolean(data.hasApiKey || loadedApiKeyId));

        // Load regions into UI
        const r = (cfg.regions || {}) as NonNullable<Config['regions']>;
        setBboxes((r?.bboxes || []).map(b => ({ minLat: String(b.minLat ?? ''), minLon: String(b.minLon ?? ''), maxLat: String(b.maxLat ?? ''), maxLon: String(b.maxLon ?? ''), name: b.name })));
        setCircles((r?.circles || []).map(c => ({ lat: String(c.lat ?? ''), lon: String(c.lon ?? ''), radiusKm: String(c.radiusKm ?? ''), name: c.name })));
        setPolygons((r?.polygons || []).map(p => ({ name: p.name, coords: (Array.isArray(p.points) ? `POLYGON((${p.points.map(pt => `${pt.lon} ${pt.lat}`).join(', ')}))` : '') })));
      } catch (e: any) {
        setError(e?.message || String(e));
      } finally { setLoadingPage(false); }
    })();
  }, []);

  function buildCleanConfig() {
    const cleanBboxes = (bboxes || [])
      .map(b => ({ minLat: parseNum(b.minLat), minLon: parseNum(b.minLon), maxLat: parseNum(b.maxLat), maxLon: parseNum(b.maxLon), name: (b.name || '').trim() || undefined }))
      .filter(b => Number.isFinite(b.minLat) && Number.isFinite(b.minLon) && Number.isFinite(b.maxLat) && Number.isFinite(b.maxLon) && b.minLat >= -90 && b.maxLat <= 90 && b.minLon >= -180 && b.maxLon <= 180 && b.minLat <= b.maxLat && b.minLon <= b.maxLon)
      .map(b => ({ ...b, minLat: Number(b.minLat), minLon: Number(b.minLon), maxLat: Number(b.maxLat), maxLon: Number(b.maxLon) }));
    const cleanCircles = (circles || [])
      .map(c => ({ lat: parseNum(c.lat), lon: parseNum(c.lon), radiusKm: parseNum(c.radiusKm), name: (c.name || '').trim() || undefined }))
      .filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lon) && Number.isFinite(c.radiusKm) && c.radiusKm > 0 && c.lat >= -90 && c.lat <= 90 && c.lon >= -180 && c.lon <= 180)
      .map(c => ({ ...c, lat: Number(c.lat), lon: Number(c.lon), radiusKm: Number(c.radiusKm) }));
    const cleanPolygons = (polygons || [])
      .map(p => {
        const name = (p.name || '').trim() || undefined;
        const raw = String(p.coords || '').trim();
        // WKT POLYGON: POLYGON((lon lat, lon lat, ...))
        // Be robust to newlines and excessive whitespace in pasted WKT
        const flat = raw.replace(/\s+/g, ' ');
        const m = /polygon\s*\(\((.+?)\)\)/i.exec(flat);
        const inner = m ? m[1] : '';
        const pairs = inner.split(',').map(s => s.trim()).filter(Boolean);
        const pts = pairs.map(pair => {
          const parts = pair.split(/\s+/).filter(Boolean);
          const lon = parseNum(parts[0]);
          const lat = parseNum(parts[1]);
          return { lat, lon };
        }).filter(pt => Number.isFinite(pt.lat) && Number.isFinite(pt.lon) && pt.lat >= -90 && pt.lat <= 90 && pt.lon >= -180 && pt.lon <= 180);
        if (pts.length >= 3) {
          const f = pts[0];
          const l = pts[pts.length - 1];
          const same = Number(f.lat) === Number(l.lat) && Number(f.lon) === Number(l.lon);
          if (!same) pts.push({ lat: f.lat, lon: f.lon });
        }
        return { name, points: pts };
      })
      .filter(p => Array.isArray(p.points) && p.points.length >= 4);
    const regions = { bboxes: cleanBboxes, circles: cleanCircles, polygons: cleanPolygons };
    return {
      enabled: true,
      webhookUrl: (config.webhookUrl || '').trim(),
      apiKeyId,
      regions,
    } as any;
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      {(loadingPage || loading) && <Spinner label="Loading..." />}
      <div aria-hidden={(loadingPage || loading)} style={{ display: (loadingPage || loading) ? 'none' : undefined }}>
        {allowed.has(svcId) && (
          <>
            {tab === 'about' && (
              <>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h3 style={{ margin: 0 }}>About</h3>
                </div>
                <div className="card" style={{ marginBottom: 12 }}>
                  <h3 style={{ marginTop: 0 }}>About this service</h3>
                  <div className="muted" style={{ marginTop: 4 }}>
                    {description}
                    <div style={{ marginTop: 8 }}>
                      Tip: Every page includes a <strong>Show Guide</strong> button to help you get oriented and understand more.
                    </div>
                  </div>
                </div>
              </>
            )}
            {tab === 'access' && (
              <div>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h3 style={{ margin: 0 }}>Access</h3>
                  <button className="btn warning" onClick={() => setShowAccessGuide(true)}>Show Guide</button>
                </div>
                <AccessPolicyCard policy={policy} svcId={svcId} />
              </div>
            )}
            {tab === 'webhook' && (
              <div>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h3 style={{ margin: 0 }}>Webhook</h3>
                  <button className="btn warning" onClick={() => setShowWebhookGuide(true)}>Show Guide</button>
                </div>
                <WebhookConfigSection
                  svc="lightning-push"
                  webhookUrl={config.webhookUrl}
                  setWebhookUrl={(s) => setConfig({ ...config, webhookUrl: s })}
                  apiKeyId={apiKeyId}
                  setApiKeyId={setApiKeyId}
                  hasSecret={hasSecret}
                  setHasSecret={setHasSecret}
                  onSaveConfig={async () => {
                    setErrRegions(''); setOkRegions(''); setLoading(true);
                    try {
                      const clean = buildCleanConfig();
                      const r = await apiFetch('/dev/lightning-push/config', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ config: clean }) });
                      if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    } finally { setLoading(false); }
                  }}
                />
              </div>
            )}
          </>
        )}

        

        {tab === 'regions' && (
        <>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Regions</h3>
            <button className="btn warning" onClick={() => setShowRegionsGuide(true)}>Show Guide</button>
          </div>
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ marginTop: 0, marginBottom: 0 }}>About Regions</h3>
              <button className="btn info" onClick={() => setShowRegionsMap(true)}>Map View</button>
            </div>
          <div className="muted" style={{ marginBottom: 6 }}>Define regions to receive notifications for. Only areas overlapping your access policy are eligible.</div>

          <h4 style={{ margin: '8px 0 6px 0' }}>Bounding Boxes</h4>
          {(bboxes || []).map((b, i) => (
            <div key={i} className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <input className="txt" placeholder="Name (optional)" value={b.name || ''} onChange={e => { const n=[...bboxes]; n[i]={...n[i], name:e.target.value}; setBboxes(n); }} />
              <input className="txt" placeholder="minLat" value={b.minLat} onChange={e => { const n=[...bboxes]; n[i]={...n[i], minLat:e.target.value}; setBboxes(n); }} />
              <input className="txt" placeholder="minLon" value={b.minLon} onChange={e => { const n=[...bboxes]; n[i]={...n[i], minLon:e.target.value}; setBboxes(n); }} />
              <input className="txt" placeholder="maxLat" value={b.maxLat} onChange={e => { const n=[...bboxes]; n[i]={...n[i], maxLat:e.target.value}; setBboxes(n); }} />
              <input className="txt" placeholder="maxLon" value={b.maxLon} onChange={e => { const n=[...bboxes]; n[i]={...n[i], maxLon:e.target.value}; setBboxes(n); }} />
              <button className="btn danger" onClick={() => { const n=[...bboxes]; n.splice(i,1); setBboxes(n); }}>Remove</button>
            </div>
          ))}
          <div className="row" style={{ gap: 8, marginBottom: 10 }}>
            <button className="btn success" onClick={() => setBboxes([...(bboxes||[]), { minLat:'', minLon:'', maxLat:'', maxLon:'', name:'' }])}>Add Bounding Box</button>
          </div>

          <h4 style={{ margin: '8px 0 6px 0' }}>Circles</h4>
          {(circles || []).map((c, i) => (
            <div key={i} className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <input className="txt" placeholder="Name (optional)" value={c.name || ''} onChange={e => { const n=[...circles]; n[i]={...n[i], name:e.target.value}; setCircles(n); }} />
              <input className="txt" placeholder="lat" value={c.lat} onChange={e => { const n=[...circles]; n[i]={...n[i], lat:e.target.value}; setCircles(n); }} />
              <input className="txt" placeholder="lon" value={c.lon} onChange={e => { const n=[...circles]; n[i]={...n[i], lon:e.target.value}; setCircles(n); }} />
              <input className="txt" placeholder="radiusKm" value={c.radiusKm} onChange={e => { const n=[...circles]; n[i]={...n[i], radiusKm:e.target.value}; setCircles(n); }} />
              <button className="btn danger" onClick={() => { const n=[...circles]; n.splice(i,1); setCircles(n); }}>Remove</button>
            </div>
          ))}
          <div className="row" style={{ gap: 8, marginBottom: 10 }}>
            <button className="btn success" onClick={() => setCircles([...(circles||[]), { lat:'', lon:'', radiusKm:'', name:'' }])}>Add Circle</button>
          </div>

          <h4 style={{ margin: '8px 0 6px 0' }}>Polygons</h4>
          {(polygons || []).map((p, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <input className="txt" placeholder="Name (optional)" value={p.name || ''} onChange={e => { const n=[...polygons]; n[i]={...n[i], name:e.target.value}; setPolygons(n); }} />
                <button className="btn danger" onClick={() => { const n=[...polygons]; n.splice(i,1); setPolygons(n); }}>Remove</button>
              </div>
              <textarea
                className="txt"
                rows={6}
                cols={100}
                style={{ width: '100%' }}
                placeholder={"WKT POLYGON, e.g. POLYGON((lon lat, lon lat, ...))"}
                value={p.coords}
                onChange={e => { const n=[...polygons]; n[i]={...n[i], coords:e.target.value}; setPolygons(n); }}
              />
              <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>Format: WKT POLYGON. Example: <code>POLYGON((30.5 -25.1, 31.0 -25.2, 31.2 -25.0, 30.5 -25.1))</code>. The first and last point must match (closed ring).</div>
            </div>
          ))}
          <div className="row" style={{ gap: 8 }}>
            <button className="btn success" onClick={() => setPolygons([...(polygons||[]), { coords:'', name:'' }])}>Add Polygon</button>
          </div>
          {errRegions && <div style={{ color: 'crimson', marginTop: 8 }}>{errRegions}</div>}
          {okRegions && <div style={{ color: 'lightgreen', marginTop: 8 }}>{okRegions}</div>}
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <button className="btn" onClick={async () => {
              setErrRegions(''); setOkRegions(''); setLoading(true);
              try {
                const clean = buildCleanConfig();
                const r = await apiFetch('/dev/lightning-push/config', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ config: clean }) });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                setOkRegions('Regions saved');
              } catch (e) { setErrRegions((e as any)?.message || String(e)); }
              finally { setLoading(false); }
            }}>Save Regions</button>
          </div>
          </div>
        </>
        )}
        {tab === 'regions' && (() => {
          // Build background policy shapes for this service
          const bySvc = (policy?.geoByService || {})[svcId];
          const geo = bySvc || (policy?.geo || { bboxes: [], circles: [] });
          const bgBboxes = (geo?.bboxes || []).map((b: any) => ({
            minLat: Number(b?.minLat), minLon: Number(b?.minLon), maxLat: Number(b?.maxLat), maxLon: Number(b?.maxLon), name: b?.name,
          }));
          const bgCircles = (geo?.circles || []).map((c: any) => ({
            lat: Number(c?.lat), lon: Number(c?.lon), radiusKm: Number(c?.radiusKm), name: c?.name,
          }));
          // Build current region shapes in numeric form
          const mapBboxes = (bboxes || []).map(b => ({ minLat: parseNum(b.minLat), minLon: parseNum(b.minLon), maxLat: parseNum(b.maxLat), maxLon: parseNum(b.maxLon), name: (b.name || '').trim() || undefined }))
            .filter(b => Number.isFinite(b.minLat) && Number.isFinite(b.minLon) && Number.isFinite(b.maxLat) && Number.isFinite(b.maxLon));
          const mapCircles = (circles || []).map(c => ({ lat: parseNum(c.lat), lon: parseNum(c.lon), radiusKm: parseNum(c.radiusKm), name: (c.name || '').trim() || undefined }))
            .filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lon) && Number.isFinite(c.radiusKm));
          const mapPolygons = (polygons || []).map(p => {
            const name = (p.name || '').trim() || undefined;
            const flat = String(p.coords || '').trim().replace(/\s+/g, ' ');
            const m = /polygon\s*\(\((.+?)\)\)/i.exec(flat);
            const inner = m ? m[1] : '';
            const pairs = inner.split(',').map(s => s.trim()).filter(Boolean);
            const points = pairs.map(pair => {
              const parts = pair.split(/\s+/).filter(Boolean);
              const lon = parseNum(parts[0]);
              const lat = parseNum(parts[1]);
              return { lat, lon };
            }).filter(pt => Number.isFinite(pt.lat) && Number.isFinite(pt.lon));
            if (points.length >= 3) {
              const f = points[0];
              const l = points[points.length - 1];
              if (!(Number(f.lat) === Number(l.lat) && Number(f.lon) === Number(l.lon))) points.push({ lat: f.lat, lon: f.lon });
            }
            return { name, points };
          }).filter(p => Array.isArray(p.points) && p.points.length >= 3);

          return (
            <GeoMapModal
              open={showRegionsMap}
              onClose={() => setShowRegionsMap(false)}
              title="Regions vs Access Policy"
              bboxes={mapBboxes as any}
              circles={mapCircles as any}
              polygons={mapPolygons as any}
              bgBboxes={bgBboxes as any}
              bgCircles={bgCircles as any}
            />
          );
        })()}
      </div>
      {/* Tutorial videos removed in favor of Show Guide modals */}
      {/* Access Onboarding */}
      <OnboardingModal
        open={showAccessGuide}
        onClose={() => setShowAccessGuide(false)}
        title="Access Tab Guide"
        steps={[
          { title: 'What you see here', body: (
            <>
              <div>- Your account’s geographic access policy for Lightning Push.</div>
              <div>- These boundaries define where notifications are allowed.</div>
              <div>- If a region you set does not overlap this policy, it will not trigger alerts.</div>
            </>
          )},
          { title: 'Map View', body: (
            <>
              <div>- Click Map View to see the access policy on a map.</div>
              <div>- Use this to visually confirm overlap with your Regions (in the Regions tab).</div>
            </>
          )},
          { title: 'Per‑service overrides', body: (
            <>
              <div>- Access can be global or service‑specific.</div>
              <div>- If a per‑service policy exists for Lightning Push, it overrides the global one.</div>
            </>
          )},
        ]}
      />
      {/* Access Onboarding (Lightning-specific) */}
      <OnboardingModal
        open={showAccessGuide}
        onClose={() => setShowAccessGuide(false)}
        title="Access Tab Guide"
        steps={[
          { title: 'What you see here', body: (<><div>- Your account’s geographic access policy for Lightning Push.</div><div>- These boundaries define where notifications are allowed.</div><div>- If a region you set does not overlap this policy, it will not trigger alerts.</div></>) },
          { title: 'Map View', body: (<><div>- Click Map View to see the access policy on a map.</div><div>- Use this to visually confirm overlap with your Regions (in the Regions tab).</div></>) },
          { title: 'Per‑service overrides', body: (<><div>- Access can be global or service‑specific.</div><div>- If a per‑service policy exists for Lightning Push, it overrides the global one.</div></>) },
        ]}
      />
      {/* Webhook Onboarding (Lightning-specific) */}
      <OnboardingModal
        open={showWebhookGuide}
        onClose={() => setShowWebhookGuide(false)}
        title="Webhook Tab Guide"
        steps={[
          { title: 'Webhook URL', body: (<><div>- Enter the HTTPS endpoint that will receive signed JSON POSTs.</div><div>- Use a stable URL; avoid query strings for easier rotation and testing.</div></>) },
          { title: 'Signing Secret', body: (<><div>- Click Generate/Regenerate to set your HMAC secret in AWS (SecureString).</div><div>- The worker signs payloads using this secret; verify headers like <code>X-AFW-Signature</code> and <code>X-AFW-Timestamp</code> on your side.</div></>) },
          { title: 'Forecast API Key', body: (<><div>- Select a key and use Reveal to see its value; Save Webhook persists the selection.</div><div>- This API Key is stored so usage can be attributed to the right key during notifications.</div></>) },
        ]}
      />
      {/* Regions Onboarding */}
      <OnboardingModal
        open={showRegionsGuide}
        onClose={() => setShowRegionsGuide(false)}
        title="Regions Tab Guide"
        steps={[
          { title: 'What Regions are', body: (
            <>
              <div>- Define the areas where lightning notifications should be considered.</div>
              <div>- Supported shapes: Bounding Boxes, Circles, and Polygons.</div>
              <div>- Only parts overlapping your Access policy can trigger notifications.</div>
            </>
          )},
          { title: 'Input expectations', body: (
            <>
              <div>- Bounding Boxes: latitude in [-90..90], longitude in [-180..180], min ≤ max.</div>
              <div>- Circles: lat/lon valid, radiusKm &gt; 0.</div>
              <div>- Use the Name field (optional) to label shapes for clarity.</div>
            </>
          )},
          { title: 'Polygons & Map View', body: (
            <>
              <div>- Polygons use WKT: <code>POLYGON((lon lat, lon lat, ...))</code>; the first and last point must match (closed ring).</div>
              <div>- Click <strong>Map View</strong> to preview your Regions over the Access policy.</div>
              <div>- Click <strong>Save Regions</strong> to persist your configuration.</div>
            </>
          )},
        ]}
      />
    </div>
  );
}
